<template>
    <div class="access-control-compact">
        <TabContainer :tabs="tabs" :activeTab.sync="activeTab">
            <template #actions="{ activeTab: currentTab }">
                <div class="tab-actions-group">
                    <!-- 사용자 관리 탭 액션 -->
                    <template v-if="currentTab === 0">
                        <CarbonInput
                            v-model="userFilters.searchKeyword"
                            variant="search"
                            placeholder="검색"
                            @input="handleUserFilterChange"
                        />
                        <CarbonSelect
                            v-model="userFilters.filterRole"
                            :options="roleFilterOptions"
                            placeholder="전체 역할"
                            :placeholderDisabled="false"
                            @change="handleUserFilterChange"
                        />
                        <CarbonSelect
                            v-model="userFilters.filterStatus"
                            :options="statusFilterOptions"
                            placeholder="전체 상태"
                            :placeholderDisabled="false"
                            @change="handleUserFilterChange"
                        />
                        <CarbonButton
                            icon-only
                            icon="fas fa-plus"
                            title="사용자 추가"
                            @click="$refs.userTabRef && $refs.userTabRef.openUserModal('create')"
                        />
                    </template>

                    <!-- 역할 관리 탭 액션 -->
                    <template v-if="currentTab === 1">
                        <CarbonButton
                            icon-only
                            icon="fas fa-plus"
                            title="역할 추가"
                            @click="$refs.roleTabRef && $refs.roleTabRef.openRoleModal('create')"
                        />
                    </template>

                    <!-- 권한 매트릭스 탭 액션 -->
                    <template v-if="currentTab === 2">
                        <div v-if="permissionHasChanges" class="change-summary">
                            <i class="fas fa-info-circle"></i>
                            <span>{{ permissionChangeSummary || "" }}</span>
                        </div>
                        <div class="preset-buttons">
                            <CarbonButton
                                variant="preset"
                                icon="fas fa-eye"
                                title="모든 권한을 읽기만으로 설정"
                                @click="$refs.permissionTabRef && $refs.permissionTabRef.applyPresetToSelection('read')"
                            >
                                읽기만
                            </CarbonButton>
                            <CarbonButton
                                variant="preset"
                                icon="fas fa-edit"
                                title="모든 권한을 읽기+쓰기로 설정"
                                @click="
                                    $refs.permissionTabRef && $refs.permissionTabRef.applyPresetToSelection('readWrite')
                                "
                            >
                                읽기+쓰기
                            </CarbonButton>
                            <CarbonButton
                                variant="preset"
                                icon="fas fa-check-double"
                                title="모든 권한을 전체 권한으로 설정"
                                @click="$refs.permissionTabRef && $refs.permissionTabRef.applyPresetToSelection('full')"
                            >
                                전체 권한
                            </CarbonButton>
                        </div>
                        <CarbonButton
                            icon="fas fa-sync"
                            @click="$refs.permissionTabRef && $refs.permissionTabRef.loadPermissionMatrix()"
                        >
                            새로고침
                        </CarbonButton>
                        <CarbonButton
                            variant="primary"
                            icon="fas fa-save"
                            :disabled="permissionSaving || !permissionHasChanges"
                            @click="$refs.permissionTabRef && $refs.permissionTabRef.savePermissionMatrix()"
                        >
                            저장
                        </CarbonButton>
                    </template>
                </div>
            </template>
            <template #default="{ activeTab: currentTab }">
                <UserManagementTab
                    v-show="currentTab === 0"
                    ref="userTabRef"
                    :roles="roles"
                    :filters="userFilters"
                    @refresh="loadRoles"
                />
                <RoleManagementTab
                    v-show="currentTab === 1"
                    ref="roleTabRef"
                    :roles="roles"
                    :loading-roles="loadingRoles"
                    @refresh="loadRoles"
                />
                <PermissionMatrixTab
                    v-show="currentTab === 2"
                    ref="permissionTabRef"
                    :roles="roles"
                    :resources="resources"
                    @refresh="loadRoles"
                    @update:saving="permissionSaving = $event"
                    @update:has-changes="permissionHasChanges = $event"
                    @update:change-summary="permissionChangeSummary = $event"
                />
            </template>
        </TabContainer>
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import { buildResourcesFromRoles } from "@/submodules/carbon-plugin/utils/permissionUtils";
import CarbonCodes from "../../../codes";
import TabContainer from "../../common/TabContainer.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonInput from "../../common/CarbonInput.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";
import UserManagementTab from "../views/UserManagementTab.vue";
import RoleManagementTab from "../views/RoleManagementTab.vue";
import PermissionMatrixTab from "../views/PermissionMatrixTab.vue";

export default {
    name: "AccessControlManager",
    components: {
        TabContainer,
        CarbonButton,
        CarbonInput,
        CarbonSelect,
        UserManagementTab,
        RoleManagementTab,
        PermissionMatrixTab,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            activeTab: 0,
            tabs: [
                { label: "사용자 관리", value: 0 },
                { label: "역할 관리", value: 1 },
                { label: "권한 매트릭스", value: 2 },
            ],
            roles: [],
            loadingRoles: false,
            resources: [],
            userFilters: {
                searchKeyword: "",
                filterRole: "",
                filterStatus: "",
                filterDepartment: "",
            },
            permissionSaving: false,
            permissionHasChanges: false,
            permissionChangeSummary: "",
        };
    },
    computed: {
        statusFilterOptions() {
            return CarbonCodes.status.getList();
        },
        roleFilterOptions() {
            return this.roles.map((r) => ({ value: r.roleIdx, text: r.roleName }));
        },
    },
    async mounted() {
        await this.loadRoles();
    },
    methods: {
        handleUserFilterChange() {
            this.$refs.userTabRef && this.$refs.userTabRef.filterUsers();
        },
        async loadRoles() {
            this.loadingRoles = true;
            try {
                const { data } = await carbonApi.accessControl.getRoles({});
                if (data?.success) {
                    const rolesList = data.data?.roles || [];
                    this.roles = rolesList
                        .map((role) => ({
                            roleIdx: role.roleIdx,
                            roleCode: role.roleCode,
                            roleName: role.roleName,
                            remarks: role.remarks,
                            sortOrder: role.sortOrder ?? 0,
                            status: role.status ?? "-",
                            permissions: role.permissions || {},
                            isSystem: role.isSystem ?? false,
                        }))
                        .sort((a, b) => a.sortOrder - b.sortOrder);
                    this.resources = buildResourcesFromRoles(rolesList);
                }
            } catch (error) {
                console.error("역할 목록 로드 실패:", error);
            } finally {
                this.loadingRoles = false;
            }
        },
    },
};
</script>

<style scoped>
.access-control-compact {
    padding: 0px 16px;
}

.tab-actions-group {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.preset-buttons {
    display: flex;
    gap: 8px;
    margin-right: auto;
}

.change-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 4px;
    font-size: 12px;
    color: #9a3412;
}
.change-summary i {
    color: #ea580c;
}
</style>
